/**
 * PartyStatusPanel - Party members status display for game sessions
 *
 * Per UX spec (lines 753-799):
 * - Show party members with HP, conditions, turn order
 * - Initiative-based ordering
 * - Current turn highlight
 * - HP bar color coding
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { BoonBadge } from '@/components/ui/boon-badge';
import { ConditionBadge } from '@/components/ui/condition-badge';
import { ResurrectionPenaltyBadge } from '@/components/ui/resurrection-penalty-badge';
import {
  CoverBadge,
  CritMarkerBadge,
  DeathSaveTallyBadge,
  HeroicInspirationBadge,
  LastSavingThrowBadge,
} from '@/components/ui/combat-status-badges';
import { StatusPill } from '@/components/ui/status-pill';
import { Button } from '@/components/ui/button';
import ImageModal from '@/components/common/ImageModal';
import { Users, Heart, ChevronDown, ChevronUp, ImageIcon } from 'lucide-react';
import { ConcentrationPill } from '../shared/ConcentrationPill';
import { RoomTypeEnum } from '@danda/shared';
import type { HealthState } from '@danda/shared';
import { useGameStore, deriveHealthState, visibleConditions } from '@/lib/stores/gameStore';
import { useLobbyStore } from '@/lib/stores/lobbyStore';
import { usePanelCollapse } from '@/lib/hooks/usePanelCollapse';
import { HpBar } from '@/components/ui/hp-bar';
import { LeaderCrown } from '@/components/ui/leader-crown';
import { getCharacterStatus } from '@/lib/character-status';

/** Health state badge styling for observable health */
const healthStateBadgeConfig: Record<HealthState, { label: string; className: string }> = {
  uninjured: { label: 'UNINJURED', className: 'bg-green-600 text-white' },
  wounded: { label: 'WOUNDED', className: 'bg-yellow-600 text-white' },
  bloodied: { label: 'BLOODIED', className: 'bg-orange-600 text-white' },
  critical: { label: 'CRITICAL', className: 'bg-red-600 text-white' },
  dying: { label: 'DYING', className: 'bg-red-900 text-white' },
  dead: { label: 'DEAD', className: 'bg-gray-800 text-gray-200' },
};

export default function PartyStatusPanel() {
  const {
    partyMembers,
    partyName: gamePartyName,
    gameSessionId,
    playerCharacterId,
    leaderCharacterId,
    partyImageUrl: gamePartyImageUrl,
    activeBoons,
  } = useGameStore();
  const { parties, currentPartyId, backgroundMediaUrl, backgroundRoomContext } = useLobbyStore();
  const [isExpanded, toggleExpanded] = usePanelCollapse('party-status');
  const [isImageExpanded, toggleImageExpanded] = usePanelCollapse('party-image');
  const [imageOverlayOpen, setImageOverlayOpen] = useState(false);
  const [memberPortraitOverlay, setMemberPortraitOverlay] = useState<{
    url: string;
    name: string;
  } | null>(null);

  // Get party name - prioritize:
  // 1. Game store (from server via game_state event)
  // 2. localStorage (stored during game transition from lobby)
  // 3. Lobby store (if still available)
  // 4. Fallback "Party"
  const derivedPartyId = gameSessionId ? gameSessionId.split('_').slice(0, -1).join('_') : null;
  const partyId = currentPartyId || derivedPartyId;
  const currentParty = partyId ? parties.find((p) => p.id === partyId) : null;

  // Try localStorage fallback (stored during lobby → game transition)
  const storedPartyInfo = (() => {
    try {
      const stored = localStorage.getItem('game-session-party');
      return stored ? (JSON.parse(stored) as { id: string; name: string }) : null;
    } catch {
      return null;
    }
  })();

  // Priority: game store > localStorage > lobby store > fallback
  // Use game store if available, else localStorage (set during lobby→game transition), else lobby store
  const partyName = gamePartyName || storedPartyInfo?.name || currentParty?.name || 'Party';

  // Get party image — prefer game store (updated on membership change) over lobby store
  const partyImageUrl =
    gamePartyImageUrl ||
    (backgroundRoomContext?.roomType === RoomTypeEnum.PARTY &&
    backgroundRoomContext?.partyId === partyId
      ? backgroundMediaUrl
      : null);

  if (partyMembers.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4" />
              {partyName}
            </CardTitle>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={toggleExpanded}
              aria-label={isExpanded ? 'Collapse panel' : 'Expand panel'}
            >
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </CardHeader>
        {isExpanded && (
          <CardContent className="flex flex-col items-center justify-center text-muted-foreground h-48">
            {partyImageUrl &&
              (isImageExpanded ? (
                <div className="relative w-full aspect-video rounded-lg overflow-hidden mb-4">
                  <img
                    src={partyImageUrl}
                    alt={`${partyName} party`}
                    className="w-full h-full object-cover"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-1 right-1 h-6 w-6 bg-black/40 hover:bg-black/60"
                    onClick={toggleImageExpanded}
                    aria-label="Collapse party image"
                  >
                    <ChevronUp className="h-3 w-3 text-white" />
                  </Button>
                </div>
              ) : (
                <div className="flex justify-end mb-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={toggleImageExpanded}
                    aria-label="Expand party image"
                  >
                    <ImageIcon className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            <Users className="h-12 w-12 mb-4 opacity-50" />
            <p className="text-sm text-center">No party members</p>
          </CardContent>
        )}
      </Card>
    );
  }

  // Sort: player's char always first, then by initiative (if available), then by name
  const sortedMembers = [...partyMembers].sort((a, b) => {
    const aIsPlayer = a.charId === playerCharacterId ? 1 : 0;
    const bIsPlayer = b.charId === playerCharacterId ? 1 : 0;
    if (aIsPlayer !== bIsPlayer) return bIsPlayer - aIsPlayer;
    if (a.initiative !== undefined && b.initiative !== undefined) {
      return b.initiative - a.initiative;
    }
    return a.name.localeCompare(b.name);
  });

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="h-4 w-4" />
            {partyName}
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={toggleExpanded}
            aria-label={isExpanded ? 'Collapse panel' : 'Expand panel'}
          >
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent className="space-y-3">
          {/* Party boons — first child so they stay under the party name even
              when the image below is expanded. Party-wide, so one row, not
              one badge per member. */}
          {activeBoons.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {activeBoons.map((boon) => (
                <BoonBadge key={boon.name} boon={boon} />
              ))}
            </div>
          )}
          {/* Party Image (collapsible; click to enlarge) */}
          {partyImageUrl &&
            (isImageExpanded ? (
              <div className="relative w-full aspect-video rounded-lg overflow-hidden mb-2">
                <img
                  src={partyImageUrl}
                  alt={`${partyName} party`}
                  className="w-full h-full object-cover cursor-pointer"
                  onClick={() => setImageOverlayOpen(true)}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-1 right-1 h-6 w-6 bg-black/40 hover:bg-black/60"
                  onClick={(e) => {
                    // Stop the click from bubbling to the underlying image,
                    // which would open the enlarge-modal while the user is
                    // trying to collapse the image.
                    e.stopPropagation();
                    toggleImageExpanded();
                  }}
                  aria-label="Collapse party image"
                >
                  <ChevronUp className="h-3 w-3 text-white" />
                </Button>
              </div>
            ) : (
              <div className="flex justify-end mb-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={toggleImageExpanded}
                  aria-label="Expand party image"
                >
                  <ImageIcon className="h-3 w-3" />
                </Button>
              </div>
            ))}
          <ImageModal
            isOpen={imageOverlayOpen}
            onClose={() => setImageOverlayOpen(false)}
            imageUrl={partyImageUrl ?? undefined}
            altText={`${partyName} party`}
          />
          {sortedMembers.map((member) => {
            const isPlayerChar = member.charId === playerCharacterId;
            const isLeader = member.charId === leaderCharacterId;
            const hpCurrent = member.hp ?? member.maxHp ?? 10;
            const hpMax = member.maxHp ?? 10;
            const status = getCharacterStatus(
              hpCurrent,
              hpMax,
              member.conditions ?? [],
              member.isDead
            );
            const healthState =
              member.healthState ??
              deriveHealthState(hpCurrent, hpMax, member.conditions, member.isDead);
            const healthBadge = healthStateBadgeConfig[healthState];
            // Only drop what the badge actually states. Filtering 'unconscious'
            // unconditionally hid a full-HP Sleep'd member entirely, because
            // that member resolves to UNINJURED.
            const displayConditions = visibleConditions(member.conditions, healthState);

            return (
              <div
                key={member.charId}
                className={`p-3 border rounded-lg transition-all border-border ${
                  member.away ? 'opacity-50' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <Avatar
                    className={`h-14 w-14 ${member.portraitUrl ? 'cursor-pointer' : ''}`}
                    onClick={
                      member.portraitUrl
                        ? () =>
                            setMemberPortraitOverlay({
                              url: member.portraitUrl!,
                              name: member.name,
                            })
                        : undefined
                    }
                  >
                    {member.avatarUrl && <AvatarImage src={member.avatarUrl} alt={member.name} />}
                    <AvatarFallback className="text-lg">
                      {member.avatarEmoji || member.name[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1.5 mb-0.5">
                      <div className="flex items-center gap-1 min-w-0">
                        {isLeader && <LeaderCrown />}
                        <span className="font-medium text-sm truncate font-character">
                          {member.name}
                        </span>
                      </div>
                      {member.away ? (
                        <StatusPill className="bg-gray-600 text-white">AWAY</StatusPill>
                      ) : isPlayerChar ? (
                        <StatusPill className={`${status.bgColor} ${status.textColor}`}>
                          {status.label}
                        </StatusPill>
                      ) : (
                        <StatusPill className={healthBadge.className}>
                          {healthBadge.label}
                        </StatusPill>
                      )}
                    </div>
                    {/* Character class/species line (observable info) */}
                    {(member.level || member.species || member.class) && (
                      <p className="text-xs text-muted-foreground truncate mb-1">
                        {member.level && `Level ${member.level}`}
                        {member.level && (member.species || member.class) && ' '}
                        {member.species}
                        {member.species && member.class && ' '}
                        {member.class}
                      </p>
                    )}
                    {isPlayerChar ? (
                      /* Player's OWN character — show exact HP bar */
                      <div className="space-y-1">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1">
                            <Heart className="h-3 w-3 text-red-500" />
                            <span className="text-xs">
                              {hpCurrent}/{hpMax}
                            </span>
                          </div>
                          {member.initiative !== undefined && (
                            <span className="text-xs text-muted-foreground">
                              Init: {member.initiative}
                            </span>
                          )}
                        </div>
                        <HpBar current={hpCurrent} max={hpMax} />
                      </div>
                    ) : (
                      /* OTHER party members — observable info only */
                      <div className="flex items-center gap-2 mt-0.5">
                        {member.initiative !== undefined && (
                          <span className="text-xs text-muted-foreground">
                            Init: {member.initiative}
                          </span>
                        )}
                      </div>
                    )}
                    {/* Every status pill shares ONE wrapping row: conditions, the
                        resurrection penalty, exhaustion, concentration, and what
                        combat just did. These used to be five stacked blocks, so a
                        character carrying a couple of them pushed the card several
                        rows taller and each block rendered at its own size. Pills
                        now flow left-to-right and wrap only when they run out of
                        width, all at `sm` — the badges default to `xs`, which is
                        why the row looked ragged before. Each badge returns null
                        when it has nothing to say, so `empty:` collapses the
                        margin and the row disappears entirely outside combat. */}
                    <div className="mt-1 flex flex-wrap items-center gap-1 empty:mt-0">
                      {displayConditions.map((condition) => (
                        <ConditionBadge
                          key={typeof condition === 'string' ? condition : condition.name}
                          condition={condition}
                          size="sm"
                        />
                      ))}
                      {member.resurrectionPenalty !== undefined &&
                        member.resurrectionPenalty > 0 && (
                          <ResurrectionPenaltyBadge
                            penalty={member.resurrectionPenalty}
                            size="sm"
                          />
                        )}
                      {member.exhaustionLevel !== undefined && member.exhaustionLevel > 0 && (
                        <StatusPill className="bg-amber-600 text-white">
                          EXHAUSTION {member.exhaustionLevel}
                        </StatusPill>
                      )}
                      {/* Not a StatusPill (it owns truncation for long spell names),
                          so it needs the sm metrics spelled out to sit level with
                          the rest of the row. */}
                      <ConcentrationPill
                        isConcentrating={!!member.isConcentrating}
                        spellName={member.concentrationSpell}
                        className="text-[10px] leading-none tracking-wide pt-[5px] pb-1"
                      />
                      <HeroicInspirationBadge hasInspiration={member.heroicInspiration} size="sm" />
                      <DeathSaveTallyBadge
                        successes={member.deathSaveSuccesses}
                        failures={member.deathSaveFailures}
                        size="sm"
                      />
                      <CritMarkerBadge
                        dealt={member.critDealtThisRound}
                        taken={member.critTakenThisRound}
                        size="sm"
                      />
                      <LastSavingThrowBadge save={member.lastSavingThrow} size="sm" />
                      <CoverBadge cover={member.cover} size="sm" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </CardContent>
      )}
      <ImageModal
        isOpen={!!memberPortraitOverlay}
        onClose={() => setMemberPortraitOverlay(null)}
        imageUrl={memberPortraitOverlay?.url}
        altText={memberPortraitOverlay?.name ?? ''}
      />
    </Card>
  );
}
